import {
  useState, useCallback, useRef, useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import handlePromise from 'utils/Promise';
import InvitationsServices from 'services/api/Invitations';

export type DownloadAllEstado = 'idle' | 'processing' | 'completed';

const POLL_INTERVAL_MS = 4000;

type DownloadAllApiPayload = {
  descargaId?: string;
  DescargaId?: string;
  estado?: string;
  Estado?: string;
  progreso?: number;
  Progreso?: number;
  total?: number;
  Total?: number;
  downloadUrl?: string;
  DownloadUrl?: string;
  error?: string;
  Error?: string;
  errorDetail?: string;
  ErrorDetail?: string;
  failedDocuments?: string[];
  FailedDocuments?: string[];
};

function readPayload( data: DownloadAllApiPayload | null | undefined ) {
  if ( !data ) return null;
  return {
    descargaId: data.descargaId ?? data.DescargaId ?? null,
    estado: data.estado ?? data.Estado,
    progreso: data.progreso ?? data.Progreso ?? 0,
    total: data.total ?? data.Total ?? 0,
    downloadUrl: data.downloadUrl ?? data.DownloadUrl,
    error: data.error ?? data.Error,
    errorDetail: data.errorDetail ?? data.ErrorDetail,
    failedDocuments: data.failedDocuments ?? data.FailedDocuments ?? [],
  };
}

const ERROR_KEYS = [
  'DOWNLOAD_ALL_INVITATION_INVALID',
  'DOWNLOAD_ALL_NO_DOCUMENTS',
  'DOWNLOAD_ALL_ALREADY_IN_PROGRESS',
  'DOWNLOAD_ALL_SHAREPOINT_ERROR',
  'DOWNLOAD_ALL_ZIP_GENERATION_ERROR',
  'DOWNLOAD_ALL_SERVICE_UNAVAILABLE',
  'DOWNLOAD_ALL_URL_EXPIRED',
] as const;

const FALLBACK_KEY = 'DOWNLOAD_ALL_SERVICE_UNAVAILABLE';

function isKnownErrorKey( key: string ): boolean {
  return ( ERROR_KEYS as readonly string[] ).includes( key );
}

const LOG_PREFIX = '[DescargarTodo]';

const useDownloadAll = (
  invitacionUid: string,
  onError?: ( message: string ) => void,
) => {
  const { t } = useTranslation();
  const [estado, setEstado] = useState<DownloadAllEstado>( 'idle' );
  const [progreso, setProgreso] = useState( 0 );
  const [total, setTotal] = useState( 0 );
  const [failedDocuments, setFailedDocuments] = useState<string[]>( [] );

  const onErrorRef = useRef( onError );
  onErrorRef.current = onError;

  const descargaIdRef = useRef<string | null>( null );
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>( null );
  const mountedRef = useRef( true );

  const stopPolling = useCallback( () => {
    if ( intervalRef.current ) {
      clearInterval( intervalRef.current );
      intervalRef.current = null;
    }
  }, [] );

  useEffect( () => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stopPolling();
    };
  }, [] );

  const showError = useCallback( (
    key: string,
    extra?: { errorDetail?: string | null } & Record<string, unknown>,
  ) => {
    const i18nKey = isKnownErrorKey( key ) ? key : FALLBACK_KEY;
    const message = t( `admin.invitations.downloadAll.errors.${i18nKey}` );
    // eslint-disable-next-line no-console
    console.error( LOG_PREFIX, 'ERROR', {
      key, message, errorDetail: extra?.errorDetail, extra,
    } );
    setEstado( 'idle' );
    onErrorRef.current?.( message );
  }, [t] );

  const poll = useCallback( () => {
    if ( !descargaIdRef.current || !mountedRef.current ) return;

    const doPoll = async () => {
      const [error, response, data] = await handlePromise(
        InvitationsServices.getDownloadAllStatus( {
          InvitacionUid: invitacionUid,
          DescargaId: descargaIdRef.current!,
        } ),
      );

      // eslint-disable-next-line no-console
      console.log( LOG_PREFIX, 'poll', {
        descargaId: descargaIdRef.current,
        ok: response?.ok,
        status: response?.status,
        error,
        raw: data,
      } );

      if ( !mountedRef.current ) return;

      if ( !response?.ok ) {
        stopPolling();
        showError( 'DOWNLOAD_ALL_SERVICE_UNAVAILABLE', { status: response?.status, error, raw: data } );
        return;
      }

      const payload = readPayload( data );
      // eslint-disable-next-line no-console
      console.log( LOG_PREFIX, 'poll parsed', payload );

      if ( payload?.estado === 'PROCESANDO' ) {
        setProgreso( payload.progreso );
        setTotal( payload.total );
      } else if ( payload?.estado === 'COMPLETADO' && payload.downloadUrl ) {
        stopPolling();
        setProgreso( payload.total );
        setEstado( 'completed' );
        setFailedDocuments(
          payload.failedDocuments?.length ? payload.failedDocuments : [],
        );

        // eslint-disable-next-line no-console
        console.log( LOG_PREFIX, 'COMPLETADO', {
          total: payload.total,
          progreso: payload.progreso,
          downloadUrl: payload.downloadUrl,
          failedDocuments: payload.failedDocuments,
        } );

        if ( payload.failedDocuments && payload.failedDocuments.length > 0 ) {
          // eslint-disable-next-line no-console
          console.warn(
            LOG_PREFIX,
            `${payload.failedDocuments.length} documento(s) no incluidos:`,
            payload.failedDocuments,
          );
        }

        const link = document.createElement( 'a' );
        link.href = payload.downloadUrl;
        link.download = '';
        link.rel = 'noopener';
        document.body.appendChild( link );
        link.click();
        document.body.removeChild( link );
        setTimeout( () => {
          if ( mountedRef.current ) setEstado( 'idle' );
        }, 3000 );
      } else if ( payload?.estado === 'ERROR' ) {
        stopPolling();
        showError( payload.error || 'DOWNLOAD_ALL_ZIP_GENERATION_ERROR', {
          ...payload,
          errorDetail: payload.errorDetail,
        } );
      }
    };

    doPoll();
    intervalRef.current = setInterval( doPoll, POLL_INTERVAL_MS );
  }, [invitacionUid, stopPolling, showError] );

  const startDownload = useCallback( async () => {
    stopPolling();
    setEstado( 'processing' );
    setProgreso( 0 );
    setFailedDocuments( [] );

    // eslint-disable-next-line no-console
    console.log( LOG_PREFIX, 'start', { invitacionUid } );

    const [error, response, data] = await handlePromise(
      InvitationsServices.startDownloadAll( { InvitacionUid: invitacionUid } ),
    );

    // eslint-disable-next-line no-console
    console.log( LOG_PREFIX, 'start response', {
      ok: response?.ok,
      status: response?.status,
      error,
      raw: data,
    } );

    if ( !mountedRef.current ) return;

    if ( !response?.ok ) {
      const key = ( typeof error === 'string' && error ) || 'DOWNLOAD_ALL_SERVICE_UNAVAILABLE';
      showError( key, { status: response?.status, error, raw: data } );
      return;
    }

    const payload = readPayload( data );
    // eslint-disable-next-line no-console
    console.log( LOG_PREFIX, 'start parsed', payload );

    if ( !payload?.descargaId ) {
      showError( 'DOWNLOAD_ALL_SERVICE_UNAVAILABLE', payload ?? undefined );
      return;
    }

    descargaIdRef.current = payload.descargaId;
    setTotal( payload.total );
    poll();
  }, [invitacionUid, stopPolling, showError, poll] );

  return {
    estado,
    progreso,
    total,
    failedDocuments,
    startDownload,
  };
};

export default useDownloadAll;
